import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, Mail } from 'lucide-react';

/**
 * ReminderOptIn - Gentle prompt asking users if they want weekly reminders
 * 
 * Shows once after user shows deeper engagement:
 * - ≥5 tasks completed (raised from 3 to avoid conflict with InstallPrompt)
 * - InstallPrompt has been shown/dismissed first (staggered)
 * 
 * Options: "Notify me" (push), "Email me", "Not now"
 * Tone: Calm, no pressure, no urgency
 */

const REMINDER_ASKED_KEY = 'wwi_reminder_asked';
const REMINDER_PREFS_KEY = 'wwi_reminder_prefs';
const TASK_COMPLETION_COUNT_KEY = 'wwi_completed_count';

// InstallPrompt keys - check if it's been resolved before showing ReminderOptIn
const INSTALL_PROMPT_DISMISSED_KEY = 'wwi_install_prompt_dismissed';
const INSTALL_PROMPT_SHOWN_KEY = 'wwi_install_prompt_shown';

// Check if we should show the opt-in prompt
export const shouldShowReminderOptIn = () => {
  // Already asked?
  if (localStorage.getItem(REMINDER_ASKED_KEY)) return false;
  
  // Stagger: Wait until InstallPrompt has been shown or dismissed
  const installPromptResolved = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY) || 
                                 localStorage.getItem(INSTALL_PROMPT_SHOWN_KEY);
  if (!installPromptResolved) return false;
  
  // Check if user has completed at least 5 tasks (raised from 3)
  const completedCount = parseInt(localStorage.getItem(TASK_COMPLETION_COUNT_KEY) || '0', 10);
  if (completedCount < 5) return false;
  
  return true;
};

// Get current reminder preferences
export const getReminderPrefs = () => {
  try {
    const stored = localStorage.getItem(REMINDER_PREFS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Save reminder preferences
export const saveReminderPrefs = (prefs) => {
  localStorage.setItem(REMINDER_PREFS_KEY, JSON.stringify(prefs));
  localStorage.setItem(REMINDER_ASKED_KEY, 'true');
};

// Mark as declined
export const declineReminders = () => {
  localStorage.setItem(REMINDER_ASKED_KEY, 'true');
  localStorage.setItem(REMINDER_PREFS_KEY, JSON.stringify({ 
    declined: true, 
    declinedAt: new Date().toISOString() 
  }));
};

// Request push notification permission and get subscription
const subscribeToPush = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('[Reminder] Push notifications not supported');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[Reminder] Push permission denied');
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    
    // Get VAPID public key from backend
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders/vapid-key`);
    if (!response.ok) {
      console.error('[Reminder] Failed to get VAPID key');
      return null;
    }
    const { publicKey } = await response.json();
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    return subscription.toJSON();
  } catch (error) {
    console.error('[Reminder] Push subscription error:', error);
    return null;
  }
};

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Save preferences to backend (for authenticated users)
const savePrefsToBackend = async (prefs, userId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders/preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, ...prefs })
    });
    return response.ok;
  } catch (error) {
    console.error('[Reminder] Failed to save prefs to backend:', error);
    return false;
  }
};

const ReminderOptIn = ({ onClose, userId, userEmail }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleNotifyMe = async () => {
    setIsLoading(true);
    setSelectedOption('push');
    
    const subscription = await subscribeToPush();
    
    if (subscription) {
      const prefs = {
        pushEnabled: true,
        emailEnabled: false,
        pushSubscription: subscription,
        optedInAt: new Date().toISOString()
      };
      
      saveReminderPrefs(prefs);
      
      if (userId) {
        await savePrefsToBackend(prefs, userId);
      }
      
      onClose('push');
    } else {
      // Push failed, offer email as fallback
      setIsLoading(false);
      setSelectedOption(null);
    }
  };

  const handleEmailMe = async () => {
    if (!userEmail) {
      // Can't send email without email address
      onClose('no-email');
      return;
    }
    
    setIsLoading(true);
    setSelectedOption('email');
    
    const prefs = {
      pushEnabled: false,
      emailEnabled: true,
      email: userEmail,
      optedInAt: new Date().toISOString()
    };
    
    saveReminderPrefs(prefs);
    
    if (userId) {
      await savePrefsToBackend(prefs, userId);
    }
    
    setIsLoading(false);
    onClose('email');
  };

  const handleNotNow = () => {
    declineReminders();
    onClose('declined');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
        onClick={handleNotNow}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl p-6 w-full max-w-sm relative"
          style={{
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
          }}
        >
          {/* Close button */}
          <button
            onClick={handleNotNow}
            className="absolute top-4 right-4 p-1 text-stone-400 hover:text-stone-600 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          {/* Content */}
          <div className="text-center mb-6">
            <p className="text-lg text-stone-700 font-medium mb-2">
              Want a gentle reminder to return to your thread?
            </p>
            <p className="text-sm text-stone-500">
              We'll check in once a week, only if you've been away.
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {/* Push notification option */}
            <button
              onClick={handleNotifyMe}
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isLoading && selectedOption === 'push'
                  ? 'bg-stone-100 text-stone-400'
                  : 'bg-[#FDFBF7] text-stone-700 hover:bg-stone-100 border border-stone-200'
              }`}
              data-testid="reminder-notify-btn"
            >
              <Bell size={16} className="text-stone-500" />
              {isLoading && selectedOption === 'push' ? 'Setting up...' : 'Notify me'}
            </button>

            {/* Email option - only show if user has email */}
            {userEmail && (
              <button
                onClick={handleEmailMe}
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isLoading && selectedOption === 'email'
                    ? 'bg-stone-100 text-stone-400'
                    : 'bg-[#FDFBF7] text-stone-700 hover:bg-stone-100 border border-stone-200'
                }`}
                data-testid="reminder-email-btn"
              >
                <Mail size={16} className="text-stone-500" />
                {isLoading && selectedOption === 'email' ? 'Setting up...' : 'Email me'}
              </button>
            )}

            {/* Not now */}
            <button
              onClick={handleNotNow}
              disabled={isLoading}
              className="w-full px-4 py-3 text-sm text-stone-500 hover:text-stone-700 transition-colors"
              data-testid="reminder-decline-btn"
            >
              Not now
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReminderOptIn;
